import type { MacroWeather, Persona, WeatherState } from "@macroscope/shared";

export interface WeatherInputData {
  quotes: Array<{ symbol: string; change_pct: number }>;
  events: Array<{ impact: string; datetime_utc: string }>;
  positions: Array<{ instrument: string; percentile: number; zscore: number }>;
  liquidity?: { chg_4w: number; chg_13w: number };
  trackers?: Array<{ scope: string; value: number; trend: string }>;
}

interface WeightedInput {
  engine: string;
  value: number; // -100..100
  weight: number;
}

function getChange(quotes: WeatherInputData["quotes"], symbol: string): number {
  return quotes.find((q) => q.symbol === symbol)?.change_pct ?? 0;
}

function clamp(n: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, n));
}

function changeToScore(change: number): number {
  // Map daily % change roughly to -100..100
  return clamp(change * 20, -100, 100);
}

function scoreToWeather(score: number): WeatherState {
  if (score >= 40) return "clear";
  if (score >= 15) return "fair";
  if (score >= -15) return "mixed";
  if (score >= -40) return "overcast";
  return "stormy";
}

function eventDensityScore(events: WeatherInputData["events"], now = new Date()): number {
  const next24h = events.filter((e) => {
    const diff = new Date(e.datetime_utc).getTime() - now.getTime();
    return diff > 0 && diff < 24 * 60 * 60 * 1000 && e.impact === "high";
  }).length;
  // More high-impact events = more uncertainty/overcast (negative)
  return clamp(20 - next24h * 15, -100, 100);
}

function positioningExtremity(positions: WeatherInputData["positions"], instruments: string[]): number {
  const relevant = positions.filter((p) => instruments.includes(p.instrument));
  if (!relevant.length) return 0;
  // High percentile (>80) or low (<20) = extremity = potential reversal = overcast
  const avg = relevant.reduce((sum, p) => sum + Math.abs(p.percentile - 50), 0) / relevant.length;
  return clamp(50 - avg * 2, -100, 100);
}

function geoTensionScore(trackers: WeatherInputData["trackers"], scopes: string[]): number {
  const relevant = trackers?.filter((t) => scopes.includes(t.scope)) ?? [];
  if (!relevant.length) return 0;
  // High tension = overcast for risk, bid for havens
  const avg = relevant.reduce((sum, t) => sum + t.value, 0) / relevant.length;
  return clamp(50 - avg, -100, 100);
}

export function computeMacroWeather(persona: Persona, data: WeatherInputData): MacroWeather {
  const now = new Date().toISOString();
  const inputs: WeightedInput[] = [];

  switch (persona) {
    case "fx_day_trader": {
      const dxy = changeToScore(getChange(data.quotes, "DXY"));
      const fx = (changeToScore(getChange(data.quotes, "EURUSD")) + changeToScore(getChange(data.quotes, "GBPUSD")) - changeToScore(getChange(data.quotes, "USDJPY"))) / 3;
      const pos = positioningExtremity(data.positions, ["EUR", "GBP", "JPY"]);
      const events = eventDensityScore(data.events);
      const liq = clamp((data.liquidity?.chg_4w ?? 0) / 100, -100, 100);
      inputs.push({ engine: "DXY", value: dxy, weight: 0.2 });
      inputs.push({ engine: "FX pairs", value: fx, weight: 0.2 });
      inputs.push({ engine: "Positioning", value: pos, weight: 0.2 });
      inputs.push({ engine: "Event density", value: events, weight: 0.2 });
      inputs.push({ engine: "Liquidity", value: liq, weight: 0.2 });
      break;
    }
    case "index_scalper": {
      const spx = changeToScore(getChange(data.quotes, "SPX"));
      const vix = clamp(-(getChange(data.quotes, "VIX") ?? 0) * 10, -100, 100);
      const liq = clamp((data.liquidity?.chg_4w ?? 0) / 100, -100, 100);
      const events = eventDensityScore(data.events);
      inputs.push({ engine: "SPX", value: spx, weight: 0.25 });
      inputs.push({ engine: "VIX", value: vix, weight: 0.25 });
      inputs.push({ engine: "Liquidity", value: liq, weight: 0.25 });
      inputs.push({ engine: "Event density", value: events, weight: 0.25 });
      break;
    }
    case "commodities_swing": {
      const gold = changeToScore(getChange(data.quotes, "XAU"));
      const oil = changeToScore(getChange(data.quotes, "WTI"));
      const geo = geoTensionScore(data.trackers, ["Middle East / Iran-Israel", "Russia-NATO/Ukraine"]);
      const dxy = changeToScore(getChange(data.quotes, "DXY"));
      inputs.push({ engine: "Gold", value: gold, weight: 0.25 });
      inputs.push({ engine: "Oil", value: oil, weight: 0.25 });
      inputs.push({ engine: "Geopolitics", value: geo, weight: 0.25 });
      inputs.push({ engine: "DXY", value: dxy, weight: 0.25 });
      break;
    }
    case "crypto_macro": {
      const btc = changeToScore(getChange(data.quotes, "BTC"));
      const liq = clamp((data.liquidity?.chg_4w ?? 0) / 100, -100, 100);
      const dxy = changeToScore(getChange(data.quotes, "DXY"));
      const spx = changeToScore(getChange(data.quotes, "SPX"));
      inputs.push({ engine: "BTC", value: btc, weight: 0.3 });
      inputs.push({ engine: "Liquidity", value: liq, weight: 0.3 });
      inputs.push({ engine: "DXY", value: dxy, weight: 0.2 });
      inputs.push({ engine: "Risk tone", value: spx, weight: 0.2 });
      break;
    }
    case "options_vol": {
      const vix = clamp((getChange(data.quotes, "VIX") ?? 0) * 10, -100, 100);
      const events = eventDensityScore(data.events);
      const spx = changeToScore(getChange(data.quotes, "SPX"));
      inputs.push({ engine: "VIX", value: vix, weight: 0.4 });
      inputs.push({ engine: "Event density", value: events, weight: 0.3 });
      inputs.push({ engine: "SPX", value: spx, weight: 0.3 });
      break;
    }
  }

  const raw = inputs.reduce((sum, i) => sum + i.value * i.weight, 0);
  const state = scoreToWeather(raw);

  const driverLines: Record<WeatherState, string> = {
    clear: "conditions are supportive — keep an eye on session liquidity.",
    fair: "bias is positive but not stretched; monitor upcoming events.",
    mixed: "cross-currents are balanced — wait for a clearer setup.",
    overcast: "headwinds building; defensive reads dominate.",
    stormy: "risk-off pressure is elevated — this is a lower-conviction environment for risk assets.",
  };

  return {
    persona,
    state,
    driver_sentence: `${persona.replace(/_/g, " ")}: ${state} — ${driverLines[state]}`,
    inputs,
    source: "MACROSCOPE fusion (Phase 1 inputs)",
    updated_at: now,
    delayed: true,
    educational: true,
  };
}
